/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { Form } from './form';
import api from 'api';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import FillForm from 'assets/icons/fillForm.png';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
    '& .MuiPaper-root': {
        width: '100%',
        maxWidth: 'none',
    },
}));

interface Field {
    name: string;
    label: string;
    type: 'text' | 'number' | 'dropdown' | 'toggle';
    validation?: yup.StringSchema<string> | yup.NumberSchema<number> | yup.BooleanSchema<boolean>;
    options?: string[];
    defaultValue?: string | number | boolean;
    value: string | number | boolean;
    fieldName: string;
}

interface FormBuilderProps {
    docId: number;
}

export default function FormBuilder({ docId }: FormBuilderProps) {
    const [fields, setFields] = useState<any[]>([]);
    const [formTitle, setFormTitle] = useState<string>('');
    const [defaultValues, setDefaultValues] = useState<Record<string, any>>({});
    const [currentPage, setCurrentPage] = useState(0);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (open) {
            getForm(docId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        setInitialValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, currentPage]);

    const setInitialValues = () => {
        const defaultVals: Record<string, any> = {};
        fields[currentPage]?.fields.forEach((field: Field) => {
            defaultVals[field.fieldName] = field.value ? field.value : field.defaultValue || '';
        });
        setDefaultValues(defaultVals);
    };

    const getForm = async (id: number) => {
        try {
            const resp = await api.governmentForms.get(id);
            if (resp.status === 200 || resp.status === 201) {
                setFields(resp?.data?.fields_json);
                setFormTitle(resp?.data?.document_name);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const nextPage = () => {
        if (currentPage < fields.length - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = (open: boolean) => {
        setOpen(open);
    };

    return (
        <>
            <button onClick={handleClickOpen}>
                <img src={FillForm} alt="fill" height="16px" />
            </button>
            <BootstrapDialog
                onClose={() => handleClose(false)}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <DialogTitle sx={{ m: 0, p: 2, mr: 10 }} id="customized-dialog-title">
                    <b>{formTitle}</b>: {fields[currentPage]?.label}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    <Typography gutterBottom>
                        <Form
                            docId={docId}
                            fields={fields[currentPage]?.fields}
                            isLastPage={currentPage === fields.length - 1}
                            currentPage={currentPage}
                            defaultValues={defaultValues}
                            resetFieldsOnPageChange={true}
                            next={nextPage}
                            prev={prevPage}
                            handleClose={handleClose}
                        />
                    </Typography>
                </DialogContent>
            </BootstrapDialog>
        </>
    );
}
